import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_INTERVAL = 2500;
const POLL_MAX = 20; // 50s total

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | expired | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  const sessionId =
    params.get("session_id") ||
    (typeof window !== "undefined" ? sessionStorage.getItem("dreamonds_last_session") : null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
            try { sessionStorage.removeItem("dreamonds_last_session"); } catch {}
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid" && data.status === "complete") {
          if (data.status === "expired") { setState("expired"); return; }
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setState("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, clear]);

  return (
    <div data-testid="checkout-success-page" className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 py-24">
        {state === "checking" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="text-center"
            data-testid="status-checking"
          >
            <Loader2 size={40} className="mx-auto text-[#C8A165] animate-spin" strokeWidth={1.2} />
            <p className="label-track text-[#C8A165] mt-8 mb-3">Confirming</p>
            <h1 className="font-heading text-4xl text-[#0A0A0A]">Verifying your payment…</h1>
            <p className="text-[#737373] mt-4 max-w-md mx-auto">
              This takes a moment. Please don't close this window.
            </p>
          </motion.div>
        )}

        {state === "paid" && (
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-center"
            data-testid="status-paid"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#C8A165] mb-8">
              <CheckCircle2 size={32} className="text-[#C8A165]" strokeWidth={1.4} />
            </div>
            <p className="label-track text-[#C8A165] mb-4">Order Confirmed</p>
            <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] leading-tight">
              Thank you.
            </h1>
            <p className="text-[#525252] mt-6 max-w-md mx-auto leading-relaxed">
              Your Dreamonds order has been received and your payment cleared successfully.
              A branded confirmation and Certificate of Authenticity will be emailed to you shortly.
            </p>

            {order && (
              <div className="mt-10 mx-auto max-w-md border border-[#EFE7D8] bg-[#FAF7F1] p-6 text-left">
                <div className="flex justify-between text-sm">
                  <span className="label-track text-[#737373]">Order</span>
                  <span className="font-mono text-[#0A0A0A]" data-testid="order-id">
                    {order.order_id?.slice(0, 8).toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-3">
                  <span className="label-track text-[#737373]">Total</span>
                  <span className="font-mono text-[#0A0A0A]" data-testid="order-total">
                    {money((order.amount_total || 0) / 100)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-3">
                  <span className="label-track text-[#737373]">Status</span>
                  <span className="font-mono text-[#0A0A0A] capitalize">{order.payment_status}</span>
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/shop"
                data-testid="continue-shopping"
                className="bg-[#0A0A0A] text-white px-8 py-4 label-track hover:bg-[#C8A165] transition-colors inline-flex items-center gap-2"
              >
                Continue Shopping <ArrowRight size={13} />
              </Link>
              <a
                href="mailto:atelier@dreamonds.com"
                className="text-[#0A0A0A] px-8 py-4 label-track shadow-[inset_0_0_0_1px_#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center gap-2"
              >
                <Mail size={13} /> Contact the Atelier
              </a>
            </div>
          </motion.div>
        )}

        {(state === "expired" || state === "error" || state === "pending") && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="text-center"
            data-testid={`status-${state}`}
          >
            <XCircle size={40} className="mx-auto text-[#B76E79]" strokeWidth={1.2} />
            <p className="label-track text-[#B76E79] mt-8 mb-3">
              {state === "expired" ? "Session Expired" : state === "pending" ? "Still Processing" : "Something Went Wrong"}
            </p>
            <h1 className="font-heading text-3xl text-[#0A0A0A]">
              {state === "expired"
                ? "Your checkout session expired."
                : state === "pending"
                ? "Your payment is still processing."
                : "We couldn't verify your payment."}
            </h1>
            <p className="text-[#737373] mt-4 max-w-md mx-auto">
              {state === "pending"
                ? "Check your email in a few minutes. you'll receive a confirmation once Stripe finalizes it."
                : "Your cart is preserved. You can try again or reach out to us for help."}
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Link
                to="/shop"
                className="bg-[#0A0A0A] text-white px-8 py-4 label-track hover:bg-[#C8A165] transition-colors"
              >
                Return to Shop
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
